import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "panel", "overlay", "frame", "nav", "prevBtn", "nextBtn"]

  // Flow cross-page phoning (10/06/2026) — arrivée depuis "suivant"/"précédent"
  // en bord de page : ?auto_open=first|last rouvre la call card sur le premier /
  // dernier contact de la nouvelle page, sans clic manuel de Sandrine.
  connect() {
    const autoOpen = new URLSearchParams(window.location.search).get("auto_open")
    if (!autoOpen) return
    const url = new URL(window.location)
    url.searchParams.delete("auto_open")
    window.history.replaceState({}, "", url) // pas de réouverture au refresh manuel
    requestAnimationFrame(() => {
      const rows = this.element.querySelectorAll("tr[data-slideover-url]")
      if (!rows.length) return
      ;(autoOpen === "last" ? rows[rows.length - 1] : rows[0]).click()
    })
  }

  open(event) {
    if (event.target.closest("a[href]")) return

    event.preventDefault()
    event.stopPropagation()

    const url = event.currentTarget.dataset.slideoverUrl
    if (!url) return

    let opener = event.currentTarget
    if (!opener.dataset.slideoverNextUrl && !opener.dataset.slideoverPrevUrl) {
      const matchingRow = Array.from(this.element.querySelectorAll("tr[data-slideover-url]"))
        .find(r => r.dataset.slideoverUrl === url)
      if (matchingRow) opener = matchingRow
    }

    this.currentPrevUrl = opener.dataset.slideoverPrevUrl || null
    this.currentNextUrl = opener.dataset.slideoverNextUrl || null
    this.currentRow = opener
    this.currentRowId = opener.id || null

    // Flow cross-page (11/06/2026) — fix bug "Session terminée" au lieu de page 2.
    // Les URLs page suivante/précédente ne sont posées QUE sur les rows de bord
    // (première = prev, dernière = next). On les capture UNE fois à l'ouverture,
    // dans des variables d'instance, depuis ces rows de bord — peu importe quel
    // contact on ouvre. Comme `log_call` remplace la row appelée SANS ré-émettre
    // ces data-attrs, relire `currentRow.dataset` après coup renvoyait null →
    // navigateNext tombait sur showSessionEnd. Capturées ici, elles survivent au
    // remplacement de row (la nav cross-page recharge la page → controller refait
    // connect/open, donc pas de stale).
    this.prevPageUrl = this.element.querySelector("tr[data-slideover-prev-page-url]")?.dataset.slideoverPrevPageUrl || null
    this.nextPageUrl = this.element.querySelector("tr[data-slideover-next-page-url]")?.dataset.slideoverNextPageUrl || null

    this.containerTarget.classList.remove("hidden")

    requestAnimationFrame(() => {
      this.containerTarget.classList.add("is-open")
      document.body.classList.add("overflow-hidden")
      this.frameTarget.src = url
      this.refreshCurrentRow()
    })
  }

  close() {
    this.containerTarget.classList.remove("is-open")
    document.body.classList.remove("overflow-hidden")

    this.panelTarget.addEventListener("transitionend", () => {
      this.containerTarget.classList.add("hidden")
      this.frameTarget.src = ""
      this.hideNavigation()
    }, { once: true })
  }

  navigatePrev() {
    if (!this.currentPrevUrl) {
      // Bord haut de page : bascule vers la page précédente si elle existe.
      if (this.prevPageUrl) window.location.href = this.prevPageUrl
      return
    }
    this.frameTarget.src = this.currentPrevUrl
    this.shiftCurrentRow(-1)
  }

  navigateNext() {
    if (!this.currentNextUrl) {
      // Bord bas de page (10/06/2026) : "suivant" coule vers la page suivante
      // (auto_open=first rouvre la call card). Si pas de page suivante = dernier
      // contact de la session → écran de fin. this.nextPageUrl capturée à l'open
      // (cf open()) → survit au remplacement de la row par log_call.
      if (this.nextPageUrl) { window.location.href = this.nextPageUrl; return }
      this.showSessionEnd()
      return
    }
    this.frameTarget.src = this.currentNextUrl
    this.shiftCurrentRow(1)
  }

  // Retour Sandrine (10/06/2026) — fin de session : message clair + retour liste
  // au lieu de boucler ou rester bloqué sur le dernier contact. Le frame est un
  // turbo-frame (pas un iframe) → on injecte directement dans innerHTML.
  showSessionEnd() {
    const treated = this.element.querySelectorAll("tr.phoning-row-called").length
    const total = this.element.querySelectorAll("tr[data-slideover-url]").length
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:280px;gap:16px;text-align:center;padding:40px;color:var(--color-text-primary);">
        <div style="font-size:46px;">🎉</div>
        <h2 style="font-size:21px;font-weight:700;margin:0;">Session terminée</h2>
        <p style="font-size:14.5px;color:var(--color-text-muted);margin:0;">
          ${treated} contact${treated !== 1 ? "s" : ""} traité${treated !== 1 ? "s" : ""} sur ${total} dans cette page.
        </p>
        <button type="button" data-action="slideover#close"
                style="margin-top:6px;padding:10px 22px;border-radius:10px;border:none;background:var(--color-gold);color:#fff;font-weight:600;font-size:14px;cursor:pointer;">
          Revenir à la liste
        </button>
      </div>`
    if (this.hasNavTarget) this.navTarget.classList.add("hidden")
  }

  shiftCurrentRow(direction) {
    const rows = Array.from(this.element.querySelectorAll("tr[data-slideover-url]"))
    let idx = this.findCurrentRowIndex(rows)

    // Fallback : si currentRow détachée du DOM (cas disqualif modale phoning où
    // turbo_stream.remove supprime la row de la liste), shiftCurrentRow retournait
    // silencieusement et currentNextUrl restait pointé sur l'URL déjà chargée
    // dans le frame → la modale "rechargeait" le même contact au prochain disqualif
    // au lieu d'avancer. Symptôme remonté par Martin (ticket 28/05) : "ne passe
    // pas à l'étape suivante". Resync via URL = celle qu'on vient de charger.
    if (idx === -1) {
      const loadedUrl = direction > 0 ? this.currentNextUrl : this.currentPrevUrl
      if (!loadedUrl) return
      const matchingRow = rows.find(r => r.dataset.slideoverUrl === loadedUrl)
      if (!matchingRow) return
      idx = rows.indexOf(matchingRow)
      this.currentRow = matchingRow
      this.currentRowId = matchingRow.id || null
      this.currentPrevUrl = matchingRow.dataset.slideoverPrevUrl ||
        (idx > 0 ? rows[idx - 1].dataset.slideoverUrl : null)
      this.currentNextUrl = matchingRow.dataset.slideoverNextUrl ||
        (idx + 1 < rows.length ? rows[idx + 1].dataset.slideoverUrl : null)
      this.updateNavigation()
      return
    }

    const newIdx = idx + direction
    if (newIdx < 0 || newIdx >= rows.length) return

    this.currentRow = rows[newIdx]
    this.currentRowId = this.currentRow.id || null
    this.currentPrevUrl = this.currentRow.dataset.slideoverPrevUrl || null
    this.currentNextUrl = this.currentRow.dataset.slideoverNextUrl || null
    this.updateNavigation()
  }

  updateNavigation() {
    // Flow cross-page (10/06/2026) : aux bords de page, la nav reste active si la
    // row courante porte une URL de page suivante/précédente (ou la fin de session
    // pour "suivant" sur le tout dernier contact).
    const canPrev = !!this.currentPrevUrl || !!this.prevPageUrl
    const canNext = !!this.currentNextUrl || !!this.nextPageUrl ||
      (this.currentRow && !this.currentNextUrl) // dernier contact = "suivant" mène à l'écran de fin
    const hasNav = canPrev || canNext
    if (this.hasNavTarget) this.navTarget.classList.toggle("hidden", !hasNav)

    // Sync TOUS les boutons nav (header slideover + call card phoning portent
    // tous deux data-slideover-target="prevBtn/nextBtn").
    this.prevBtnTargets.forEach((b) => { b.disabled = !canPrev })
    this.nextBtnTargets.forEach((b) => { b.disabled = !canNext })
  }

  hideNavigation() {
    if (this.hasNavTarget) this.navTarget.classList.add("hidden")
    this.currentPrevUrl = null
    this.currentNextUrl = null
    this.prevPageUrl = null
    this.nextPageUrl = null
    this.currentRow = null
    this.currentRowId = null
  }

  next() {
    this.refreshCurrentRow()
    this.navigateNext()
  }

  findCurrentRowIndex(rows) {
    let idx = rows.indexOf(this.currentRow)
    if (idx === -1 && this.currentRowId) {
      const freshRow = document.getElementById(this.currentRowId)
      if (freshRow) {
        this.currentRow = freshRow
        idx = rows.indexOf(freshRow)
      }
    }
    return idx
  }

  refreshCurrentRow() {
    const rows = Array.from(this.element.querySelectorAll("tr[data-slideover-url]"))
    const idx = this.findCurrentRowIndex(rows)
    if (idx === -1) return

    this.currentPrevUrl = this.currentRow.dataset.slideoverPrevUrl ||
      (idx > 0 ? rows[idx - 1].dataset.slideoverUrl : null)
    this.currentNextUrl = this.currentRow.dataset.slideoverNextUrl ||
      (idx + 1 < rows.length ? rows[idx + 1].dataset.slideoverUrl : null)
    this.updateNavigation()
  }

  reloadContent() {
    if (this.frameTarget.src) {
      const src = this.frameTarget.src
      this.frameTarget.src = ""
      this.frameTarget.src = src
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && !this.containerTarget.classList.contains("hidden")) {
      this.close()
    }
  }

  closeOnOverlay(event) {
    if (event.target === this.overlayTarget) this.close()
  }

  disconnect() {
    document.body.classList.remove("overflow-hidden")
  }
}
